/**
 * Visualiser Registry
 *
 * Visualisers are pure renderers driven by node data.
 * Registered by plugins, referenced by name in stylesheets.
 *
 * Base mappings are plugin-declared defaults (nodeType -> visualiserName).
 * Stylesheets can override these mappings.
 */

import type { ReactNode } from 'react';
import type { NodeState } from '@/runtime/RuntimeStore';
import type { Three } from '@/plugins/threejs/types';

// ============ Types ============

export type VisualiserLayer = 'background' | 'default' | 'foreground';

export interface VisualiserContext {
  is3d: boolean;
}

/** 3D render interface — visualisers never import Three.js directly */
export interface IRender3D {
  /** Called once with the Three namespace, returns an Object3D to add to the scene */
  createMesh(three: Three): any;
  /** Called per-frame with live data */
  update(data: VisualiserData): void;
  /** Cleanup GPU resources */
  dispose(): void;
}

export interface MappedValue {
  endpointId: string;
  min: number;
  max: number;
  value: any;
}

export interface VisualiserData {
  node: NodeState;
  instanceId: string;
  position: { x: number; y: number };
  values: Record<string, MappedValue>;
  tweakValue: (instanceId: string, nodeId: string, endpointId: string, value: any) => void;
}

export interface Visualiser {
  render: (data: VisualiserData, ctx: VisualiserContext) => ReactNode;
  /** Optional 3D factory — returns an IRender3D bound to one node */
  render3d?: (data: VisualiserData) => IRender3D;
  layer: VisualiserLayer;
}

// ============ Layer z-index mapping ============

const LAYER_Z_INDEX: Record<VisualiserLayer, number> = {
  background: -1,
  default: 0,
  foreground: 1,
};

export function layerToZIndex(layer: VisualiserLayer): number {
  return LAYER_Z_INDEX[layer];
}

// ============ Registry ============

const visualiserRegistry: Record<string, Visualiser> = {};
/** visualiserName + param mappings (visualiser prop name -> endpoint id) */
interface BaseMapping {
  visualiserName: string;
  params: Record<string, string>;
}

const baseMappings: Record<string, BaseMapping> = {};

export function registerVisualiser(name: string, visualiser: Visualiser): void {
  visualiserRegistry[name] = visualiser;
}

export function getVisualiser(name: string): Visualiser | null {
  return visualiserRegistry[name] ?? null;
}

export function getAvailableVisualisers(): string[] {
  return Object.keys(visualiserRegistry);
}

/**
 * Register a base mapping from node type to visualiser.
 * @param params - Maps visualiser property names to endpoint ids (e.g. { radius: 'frequency' })
 */
export function registerBaseMapping(nodeType: string, visualiserName: string, params?: Record<string, string>): void {
  baseMappings[nodeType] = { visualiserName, params: params ?? {} };
}

export function getBaseMapping(nodeType: string): BaseMapping | undefined {
  return baseMappings[nodeType];
}
